import useAuth from 'hooks/useAuth.ts';
import { useMemo } from 'react';
import { FeatureFlag } from 'types/features';
import { useIsFeatureEnabled } from './useFeatureFlags';

export const useHasPermissions = (userPermissions: string[]) => {
    const { user } = useAuth();
    const permissions = user?.permissions;
    const hasAnyPermission = useMemo(
        () => permissions?.find((permission) => userPermissions.includes(permission.code)),
        [permissions, userPermissions]
    );
    return !!hasAnyPermission;
};

/**
 * The matter-assignment endpoints require READ.MATTER_ASSIGNMENTS, which is independent of the
 * assignment feature flag. Fetching assignments without both leaves the caller with an unusable
 * response — either an "Access Denied" error or data no view is allowed to render.
 */
export const useCanReadMatterAssignments = () => {
    const isAssignmentFeatureEnabled = useIsFeatureEnabled(FeatureFlag.CASE_ASSIGNMENT_ENABLED);
    const hasReadAssignmentsPermission = useHasPermissions(['READ.MATTER_ASSIGNMENTS']);

    return isAssignmentFeatureEnabled && hasReadAssignmentsPermission;
};

/**
 * CREATE.MATTERS is the blanket grant; the two relationship-scoped grants each open only their own
 * kind of client party. A non-client party is reachable by the blanket grant alone.
 */
export const useCreateMatterPermissions = () => {
    const hasCreateMatters = useHasPermissions(['CREATE.MATTERS']);
    const hasCreateMattersLeads = useHasPermissions(['CREATE.MATTERS.LEADS']);
    const hasCreateMattersClients = useHasPermissions(['CREATE.MATTERS.CLIENTS']);

    return {
        hasCreateMatters,
        hasCreateMattersLeads,
        hasCreateMattersClients,
        canCreateForClients: hasCreateMatters || hasCreateMattersClients,
        canCreateForLeads: hasCreateMatters || hasCreateMattersLeads,
        hasAnyCreateMatterPermission: hasCreateMatters || hasCreateMattersLeads || hasCreateMattersClients,
    };
};
